import { FindingsAlert, FindingsSelector } from '@app/icare/components/findings'
import { RelationStatusTag } from '@app/icare/components/status/RelationStatusTag'
import { patientADTeamQueries } from '@app/icare/entities/adTeam/queries/patientADTeamQueries'
import { selectByGroups } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { patientCareManagerQueries } from '@app/icare/entities/user/queries/patientCareManagerQueries'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { Trans } from '@lingui/react/macro'
import { ActionColumnButton } from '@pkg/ui/button/ActionButton'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Flex, Table } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { useMemo } from 'react'

export const Route = createFileRoute('/_app/patienten/$id/_edit/zustandigkeit/')({
  component: RouteComponent,
})

interface ZustandigkeitRow {
  type: 'careManagers' | 'adTeams'
  name?: string
  activeFrom?: string | null
  activeTo?: string | null
}

function RouteComponent() {
  const { id: patientId } = Route.useParams()
  const { data: adTeams = [], isLoading: isAdTeamsLoading } = useQuery(patientADTeamQueries.forIndex(patientId))
  const { data: careManagers = [], isLoading: isCareManagersLoading } = useQuery(
    patientCareManagerQueries.forIndex(patientId),
  )

  const rows = useMemo<ZustandigkeitRow[]>(() => {
    const result = []

    result.push(
      ...adTeams
        .filter(adTeam => isActivePatientRelation(adTeam))
        .map(adTeam => ({
          type: 'adTeams' as const,
          name: adTeam.to?.name,
          activeFrom: adTeam.activeFrom,
          activeTo: adTeam.activeTo,
        })),
    )

    result.push(
      ...careManagers
        .filter(careManager => isActivePatientRelation(careManager))
        .map(careManager => ({
          type: 'careManagers' as const,
          name: careManager.to?.name,
          activeFrom: careManager.activeFrom,
          activeTo: careManager.activeTo,
        })),
    )

    return result
  }, [adTeams, careManagers])

  const columns: ColumnType<ZustandigkeitRow>[] = [
    {
      key: 'type',
      title: <Trans>Art</Trans>,
      fixed: 'start',
      render: (_, record) =>
        record.type === 'careManagers' ? <Trans>Versorgungsmanager</Trans> : <Trans>AD-Team</Trans>,
    },
    { key: 'name', title: <Trans>Name</Trans>, render: (_, record) => record.name },
    {
      key: 'status',
      title: <Trans>Zuständigkeit</Trans>,
      render: (_, record) => <RelationStatusTag value={record} />,
    },
    {
      key: 'actions',
      title: <Trans>Aktionen</Trans>,
      fixed: 'end',
      render: (_, record) => (
        <ActionColumnButton
          to={`./${record.type === 'careManagers' ? 'versorgungsmanager' : 'ad-team'}`}
          type="showEntity"
        />
      ),
    },
  ]

  return (
    <Flex vertical gap="small">
      <FindingsSelector
        selector={selectByGroups(['careManagers', 'adTeams'])}
        children={f => <FindingsAlert findings={f} />}
      />
      <Table<ZustandigkeitRow>
        dataSource={rows}
        rowKey="type"
        columns={columns}
        size="small"
        pagination={false}
        loading={isAdTeamsLoading || isCareManagersLoading}
        scroll={{ x: 'max-content' }}
      />
    </Flex>
  )
}
